/* eslint-disable max-lines, max-lines-per-function */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { TableUtility } from '@utility/dynamic-table';
import lottie from 'lottie-web';
import { toast } from 'react-toastify';
import { useAuth } from '../../../Hooks/useAuth';
import UseAxios from '../../../Api/http/http';
import normalizeTableUtilityResponse from '../../../Utils/tableUtilityResponse';
import {
  createShiftAllowanceDetail,
  deleteShiftAllowanceDetail,
  fetchShiftAllowanceEmployees,
  getShiftAllowanceBaseURL,
  submitShiftAllowanceApproval,
  updateShiftAllowanceDetail,
  uploadShiftAllowanceDetails,
} from '../../../Api/request/shiftAllowance';
import { getProjectFileUploadSampleFile } from '../../../Api/request/projectFileUpload';
import ShiftTimePicker from '../../../Components/ShiftTimePicker/ShiftTimePicker';
import { MODULE_NAMES } from '../../../Constants/modules';
import emptyStateAnimationData from '../../../Assets/images/json/dar_no_data.json';
import alertIcon from '../../../Assets/images/svg/alert_icon.svg';
import darAddIcon from '../../../Assets/images/svg/dar_add.svg';
import darDeleteIcon from '../../../Assets/images/svg/dar_delete.svg';
import darEditIcon from '../../../Assets/images/svg/dar_edit.svg';
import './ShiftAllowance.scss';

import 'primeicons/primeicons.css';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';

const PAGE_SIZE = 10;
const PAGINATION_CONFIG = { page: 1, limit: PAGE_SIZE, rowsPerPageOptions: [10, 20, 30, 40] };
const SHIFT_ALLOWANCE_TABLE_HEIGHT = '430px';
const SHIFT_ALLOWANCE_SAMPLE_FILE_TYPE = 'shift_allowance';
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const normalizePermissionAction = (action = '') => action.toString().trim()
  .toLowerCase();

const getPermissionActions = (permissions = {}, moduleName = '') => {
  const moduleCandidates = [
    moduleName,
    moduleName.replace(/-/g, '_'),
    moduleName.replace(/_/g, '-'),
  ];

  for (const candidate of moduleCandidates) {
    if (Array.isArray(permissions?.[candidate])) {
      return permissions[candidate].map(normalizePermissionAction);
    }
  }

  return [];
};

const hasPermissionAction = (permissions = {}, moduleName = '', action = '') => (
  getPermissionActions(permissions, moduleName).includes(normalizePermissionAction(action))
);
const SHIFT_ALLOWANCE_UPLOAD_HEADERS = [
  'Name',
  'Emp ID',
  'Morning (No of Days)',
  'Afternoon (No of Days)',
  'Night (No of Days)',
  'Group',
  'Timings',
  'Extra Hours',
  'Total Hours',
];

const hasFilterValue = (item) => {
  if (!item || item?.type !== 'filter') return false;
  if (Array.isArray(item?.selectedValues) && item.selectedValues.length > 0) return true;
  if (Array.isArray(item?.range)) return item.range.some(Boolean);
  return Boolean(item?.selectedValue ?? item?.inputValue ?? item?.value);
};

const getAppliedFiltersFromConfiguration = (tableConfiguration = []) => (
  tableConfiguration
    .flatMap((column) => column?.menuItems || [])
    .flatMap((menuItem) => menuItem?.items || [])
    .filter(hasFilterValue)
);

const getFilterValue = (item) => {
  if (!item) return '';
  if (Array.isArray(item.selectedValues) && item.selectedValues.length > 0) {
    const [selectedValue] = item.selectedValues;
    if (selectedValue && typeof selectedValue === 'object') {
      return selectedValue.label || selectedValue.value || '';
    }
    return selectedValue || '';
  }

  if (item.selectedValue && typeof item.selectedValue === 'object') {
    return item.selectedValue.label || item.selectedValue.value || '';
  }

  if (item.value && typeof item.value === 'object') {
    return item.value.label || item.value.value || '';
  }

  return item.selectedValue || item.value || item.inputValue || '';
};

const getFilterRawValue = (item) => {
  if (!item) return '';
  if (Array.isArray(item.selectedValues) && item.selectedValues.length > 0) {
    const [selectedValue] = item.selectedValues;
    if (selectedValue && typeof selectedValue === 'object') {
      return selectedValue.value || selectedValue.id || selectedValue.label || '';
    }
    return selectedValue || '';
  }

  if (item.selectedValue && typeof item.selectedValue === 'object') {
    return item.selectedValue.value || item.selectedValue.id || item.selectedValue.label || '';
  }

  if (item.value && typeof item.value === 'object') {
    return item.value.value || item.value.id || item.value.label || item.value;
  }

  return item.selectedValue || item.value || item.inputValue || '';
};

const formatMonthHeading = (value) => {
  if (!value) return '';
  const rawValue = `${value}`.trim();
  const numericMonthYearMatch = rawValue.match(/^(\d{1,2})\/(\d{4})$/);

  if (numericMonthYearMatch) {
    const monthIndex = Number(numericMonthYearMatch[1]) - 1;
    return `${MONTHS_SHORT[monthIndex] || numericMonthYearMatch[1]} ${numericMonthYearMatch[2]}`;
  }

  const namedMonthYearMatch = rawValue.match(/^([A-Za-z]{3,})\s*-\s*(\d{4})$/);
  if (namedMonthYearMatch) return `${namedMonthYearMatch[1].slice(0, 3)} ${namedMonthYearMatch[2]}`;

  const date = new Date(rawValue);
  if (!Number.isNaN(date.getTime())) {
    return `${MONTHS_SHORT[date.getMonth()]} ${date.getFullYear()}`;
  }

  return rawValue;
};

const getStatusClassName = (status = '') => (
  `shift-allowance-status-badge--${`${status}`.trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')}`
);

const EMPTY_SHIFT_FORM = {
  empNo: '',
  employeeName: '',
  employeeId: '',
  morningShiftDays: '',
  afternoonShiftDays: '',
  nightShiftDays: '',
  groupName: '',
  shiftStartTime: '',
  shiftEndTime: '',
  extraHours: '',
  totalHours: '',
};

const EMPTY_APPROVER_FORM = {
  initiator: '',
  l1Approver: '',
  l2Approver: '',
};

const getEmployeeDetailLabel = (employee = {}) => employee.employeeName || '';

const toBackendTime = (value = '') => {
  const match = `${value}`.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return '';
  let hours = Number(match[1]);
  const minutes = match[2];
  const period = match[3].toUpperCase();

  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;

  return `${String(hours).padStart(2, '0')}:${minutes}:00`;
};

const toEditableCellValue = (value) => {
  const normalizedValue = `${value ?? ''}`.trim();
  return normalizedValue === '-' ? '' : normalizedValue;
};

const buildShiftFormFromRow = (rowData = {}) => ({
  empNo: rowData.empNo || rowData.emp_no || rowData.employeeNo || '',
  employeeName: toEditableCellValue(rowData.employeeName),
  employeeId: toEditableCellValue(rowData.employeeId),
  morningShiftDays: toEditableCellValue(rowData.morningShiftDays),
  afternoonShiftDays: toEditableCellValue(rowData.afternoonShiftDays),
  nightShiftDays: toEditableCellValue(rowData.nightShiftDays),
  groupName: toEditableCellValue(rowData.groupName),
  shiftStartTime: toEditableCellValue(rowData.shiftStartTime),
  shiftEndTime: toEditableCellValue(rowData.shiftEndTime),
  extraHours: toEditableCellValue(rowData.extraHours),
  totalHours: toEditableCellValue(rowData.totalHours),
});

const parseShiftAllowanceUploadFile = async (file) => {
  const Papa = (await import('papaparse')).default;

  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => `${header || ''}`.trim(),
      complete: (result) => {
        const parsedRows = Array.isArray(result?.data) ? result.data : [];
        const metaFields = result?.meta?.fields || [];
        const missingHeaders = SHIFT_ALLOWANCE_UPLOAD_HEADERS.filter(
          (header) => !metaFields.includes(header),
        );

        if (result?.errors?.length) {
          reject(new Error(result.errors[0]?.message || 'Unable to read the upload file.'));
          return;
        }
        if (missingHeaders.length > 0) {
          reject(new Error(`Missing required columns: ${missingHeaders.join(', ')}`));
          return;
        }

        const uploadRows = parsedRows
          .map((row, index) => ({
            rowNumber: index + 2,
            name: row.Name,
            employeeId: row['Emp ID'],
            morningShiftDays: row['Morning (No of Days)'],
            afternoonShiftDays: row['Afternoon (No of Days)'],
            nightShiftDays: row['Night (No of Days)'],
            groupName: row.Group,
            timings: row.Timings,
            extraHours: row['Extra Hours'],
            totalHours: row['Total Hours'],
          }))
          .filter((row) => Object.entries(row)
            .filter(([key]) => key !== 'rowNumber')
            .some(([, value]) => `${value ?? ''}`.trim()));

        if (uploadRows.length === 0) {
          reject(new Error('The uploaded file does not contain any data rows.'));
          return;
        }

        resolve(uploadRows);
      },
      error: () => reject(new Error('Unable to read the upload file.')),
    });
  });
};

const ShiftAllowanceToolbarHeading = ({
  title,
  status,
  statusClassName,
  subtitle,
}) => (
  <span className="shift-allowance-heading">
    <span className="shift-allowance-heading__top">
      <span className="shift-allowance-heading__title">{title}</span>
      {status && (
        <span className={`shift-allowance-heading__status ${statusClassName}`}>
          {status}
        </span>
      )}
    </span>
    {subtitle && (
      <span className="shift-allowance-heading__subtitle">{subtitle}</span>
    )}
  </span>
);

ShiftAllowanceToolbarHeading.propTypes = {
  title: PropTypes.string.isRequired,
  status: PropTypes.string,
  statusClassName: PropTypes.string,
  subtitle: PropTypes.string,
};

ShiftAllowanceToolbarHeading.defaultProps = {
  status: '',
  statusClassName: '',
  subtitle: '',
};

const EmptyState = ({ message }) => {
  const animationContainer = useRef(null);

  useEffect(() => {
    if (!animationContainer.current) return undefined;

    const animation = lottie.loadAnimation({
      container: animationContainer.current,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData: emptyStateAnimationData,
    });

    return () => animation.destroy();
  }, []);

  return (
    <div className="shift-allowance-empty-state-outer">
      <div className="shift-allowance-empty-state">
        <div ref={animationContainer} className="shift-allowance-empty-state__animation" />
        <p className="shift-allowance-empty-state__text">{message}</p>
      </div>
    </div>
  );
};

EmptyState.propTypes = {
  message: PropTypes.string.isRequired,
};

const ShiftAllowanceMoreMenu = ({
  isUploadDisabled,
  onHistoryClick,
  onUploadClick,
  showHistory,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen]);

  return (
    <div ref={menuRef} className="shift-allowance-more-menu">
      <button
        type="button"
        aria-label="Shift allowance more actions"
        className={`shift-allowance-more-menu__button${isOpen ? ' active' : ''}`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <i className="pi pi-ellipsis-v" />
      </button>
      {isOpen && (
        <div className="shift-allowance-more-menu__panel">
          {showHistory && (
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                onHistoryClick();
              }}
            >
              Allowance Request History
            </button>
          )}
          <button
            type="button"
            disabled={isUploadDisabled}
            onClick={() => {
              if (isUploadDisabled) return;
              setIsOpen(false);
              onUploadClick();
            }}
          >
            Upload Shift Allowance Data
          </button>
        </div>
      )}
    </div>
  );
};

ShiftAllowanceMoreMenu.propTypes = {
  isUploadDisabled: PropTypes.bool.isRequired,
  onHistoryClick: PropTypes.func.isRequired,
  onUploadClick: PropTypes.func.isRequired,
  showHistory: PropTypes.bool.isRequired,
};

const ShiftAllowanceAddButton = ({ disabled, onClick }) => (
  <button
    type="button"
    aria-label="Add shift allowance"
    className="shift-allowance-add-button"
    disabled={disabled}
    onClick={onClick}
  >
    <img src={darAddIcon} alt="" aria-hidden="true" />
  </button>
);

ShiftAllowanceAddButton.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

const ShiftAllowanceRowActions = ({
  canDelete,
  canUpdate,
  rowData,
  onAction,
}) => {
  if (rowData?.isTotalRow) return null;
  const isSubmitted = Boolean(rowData?.requestDbId || rowData?.requestId);

  return (
    <span className="shift-allowance-row-actions" onClick={(event) => event.stopPropagation()}>
      {canUpdate ? (
        <button
          type="button"
          aria-label="Edit shift allowance"
          title={isSubmitted ? 'Submitted rows cannot be edited' : 'Edit'}
          className="shift-allowance-row-actions__button"
          disabled={isSubmitted}
          onClick={(event) => onAction({ event, action: 'edit', rowData })}
        >
          <img src={darEditIcon} alt="" aria-hidden="true" />
        </button>
      ) : null}
      {canDelete ? (
        <button
          type="button"
          aria-label="Delete shift allowance"
          title={isSubmitted ? 'Submitted rows cannot be deleted' : 'Delete'}
          className="shift-allowance-row-actions__button"
          disabled={isSubmitted}
          onClick={(event) => onAction({ event, action: 'delete', rowData })}
        >
          <img src={darDeleteIcon} alt="" aria-hidden="true" />
        </button>
      ) : null}
    </span>
  );
};

ShiftAllowanceRowActions.propTypes = {
  canDelete: PropTypes.bool.isRequired,
  canUpdate: PropTypes.bool.isRequired,
  rowData: PropTypes.shape({
    isTotalRow: PropTypes.bool,
    requestDbId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    requestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  onAction: PropTypes.func.isRequired,
};

const ShiftAllowanceDropdownField = ({
  className,
  helperText,
  label,
  placeholder,
  required,
  value,
  options,
  onSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const fieldRef = useRef(null);
  const filteredOptions = options.filter((option) => (
    `${option.label || ''}`.toLowerCase().includes(searchValue.trim().toLowerCase())
  ));

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (fieldRef.current && !fieldRef.current.contains(event.target)) setIsOpen(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setSearchValue('');
  }, [isOpen]);

  return (
    <label
      ref={fieldRef}
      className={[
        'shift-allowance-dialog__field',
        className,
      ].filter(Boolean).join(' ')}
    >
      <span>
        {label}
        {required ? '*' : ''}
      </span>
      <span className="shift-allowance-dialog__input-wrap shift-allowance-dialog__input-wrap--dropdown">
        <button type="button" onClick={() => setIsOpen((prev) => !prev)}>
          <span className={value ? '' : 'placeholder'}>{value || placeholder}</span>
          <i className="pi pi-chevron-down" />
        </button>
        {isOpen && (
          <span className="shift-allowance-dialog__dropdown">
            <span className="shift-allowance-dialog__dropdown-search">
              <i className="pi pi-search" aria-hidden="true" />
              <input
                type="text"
                placeholder="Search..."
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                onClick={(event) => event.stopPropagation()}
              />
            </span>
            {filteredOptions.length === 0 ? (
              <span className="shift-allowance-dialog__dropdown-empty">No options found</span>
            ) : filteredOptions.map((option) => (
              <button
                key={`${option.value}-${option.employeeId}`}
                type="button"
                onClick={() => {
                  onSelect(option);
                  setIsOpen(false);
                }}
              >
                {option.label}
              </button>
            ))}
          </span>
        )}
      </span>
      {helperText && (
        <span className="shift-allowance-dialog__helper">{helperText}</span>
      )}
    </label>
  );
};

ShiftAllowanceDropdownField.propTypes = {
  className: PropTypes.string,
  helperText: PropTypes.string,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  required: PropTypes.bool,
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
    employeeId: PropTypes.string,
  })),
  onSelect: PropTypes.func.isRequired,
};

ShiftAllowanceDropdownField.defaultProps = {
  className: '',
  helperText: '',
  required: false,
  value: '',
  options: [],
};

const ShiftAllowanceFormField = ({
  label,
  placeholder,
  required,
  value,
  onChange,
}) => (
  <label className="shift-allowance-dialog__field">
    <span>
      {label}
      {required ? '*' : ''}
    </span>
    <span className="shift-allowance-dialog__input-wrap">
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </span>
  </label>
);

ShiftAllowanceFormField.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  required: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

ShiftAllowanceFormField.defaultProps = {
  required: false,
  value: '',
};

const ShiftAllowanceTimeField = ({ label, placeholder, required, value, onChange }) => (
  <label className="shift-allowance-dialog__field">
    <span>
      {label}
      {required ? '*' : ''}
    </span>
    <span className="shift-allowance-dialog__input-wrap shift-allowance-dialog__input-wrap--time">
      <ShiftTimePicker value={value} placeholder={placeholder} onChange={onChange} />
    </span>
  </label>
);

ShiftAllowanceTimeField.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  required: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

ShiftAllowanceTimeField.defaultProps = {
  required: false,
  value: '',
};

const AddShiftDetailsDialog = ({
  employeeOptions,
  form,
  onChange,
  onEmployeeSelect,
  onClose,
  onSave,
  open,
  title,
}) => {
  if (!open) return null;

  return (
    <div className="shift-allowance-dialog-overlay" role="presentation">
      <div
        className="shift-allowance-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="shift-allowance-add-title"
      >
        <div className="shift-allowance-dialog__header">
          <h2 id="shift-allowance-add-title">{title}</h2>
        </div>
        <div className="shift-allowance-dialog__body">
          <ShiftAllowanceDropdownField
            label="Employee Name"
            placeholder="Enter Employee Name"
            required
            value={form.employeeName}
            options={employeeOptions.map((option) => ({ ...option, label: option.employeeName }))}
            onSelect={onEmployeeSelect}
          />
          <ShiftAllowanceDropdownField
            label="Employee ID"
            placeholder="Enter Employee ID"
            required
            value={form.employeeId}
            options={employeeOptions.map((option) => ({ ...option, label: option.employeeId }))}
            onSelect={onEmployeeSelect}
          />
          <ShiftAllowanceFormField
            label="Morning Shift"
            placeholder="Enter No. of Days"
            value={form.morningShiftDays}
            onChange={(value) => onChange('morningShiftDays', value)}
          />
          <ShiftAllowanceFormField
            label="Afternoon Shift"
            placeholder="Enter No. of Days"
            value={form.afternoonShiftDays}
            onChange={(value) => onChange('afternoonShiftDays', value)}
          />
          <ShiftAllowanceFormField
            label="Night Shift"
            placeholder="Enter No. of Days"
            value={form.nightShiftDays}
            onChange={(value) => onChange('nightShiftDays', value)}
          />
          <ShiftAllowanceFormField
            label="Group Name"
            placeholder="Enter Group Name"
            required
            value={form.groupName}
            onChange={(value) => onChange('groupName', value)}
          />
          <ShiftAllowanceTimeField
            label="Shift Start Time"
            placeholder="Choose Shift Start Time"
            required
            value={form.shiftStartTime}
            onChange={(value) => onChange('shiftStartTime', value)}
          />
          <ShiftAllowanceTimeField
            label="Shift End Time"
            placeholder="Choose Shift End Time"
            required
            value={form.shiftEndTime}
            onChange={(value) => onChange('shiftEndTime', value)}
          />
          <ShiftAllowanceFormField
            label="Extra Hours"
            placeholder="Enter Extra Hours"
            required
            value={form.extraHours}
            onChange={(value) => onChange('extraHours', value)}
          />
          <ShiftAllowanceFormField
            label="Total Hours"
            placeholder="Enter Total Hours"
            required
            value={form.totalHours}
            onChange={(value) => onChange('totalHours', value)}
          />
        </div>
        <div className="shift-allowance-dialog__footer">
          <button
            type="button"
            className="shift-allowance-dialog__cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="shift-allowance-dialog__save"
            onClick={onSave}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

AddShiftDetailsDialog.propTypes = {
  employeeOptions: PropTypes.arrayOf(PropTypes.shape({
    empNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    employeeName: PropTypes.string,
    employeeId: PropTypes.string,
  })).isRequired,
  form: PropTypes.shape({
    empNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    employeeName: PropTypes.string,
    employeeId: PropTypes.string,
    morningShiftDays: PropTypes.string,
    afternoonShiftDays: PropTypes.string,
    nightShiftDays: PropTypes.string,
    groupName: PropTypes.string,
    shiftStartTime: PropTypes.string,
    shiftEndTime: PropTypes.string,
    extraHours: PropTypes.string,
    totalHours: PropTypes.string,
  }).isRequired,
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onEmployeeSelect: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

const DeleteShiftAllowanceDialog = ({
  open,
  onClose,
  onDelete,
}) => {
  if (!open) return null;

  return (
    <div className="shift-allowance-dialog-overlay" role="presentation">
      <div
        className="shift-allowance-delete-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="shift-allowance-delete-title"
      >
        <div className="shift-allowance-dialog__header">
          <h2 id="shift-allowance-delete-title">Delete Shift Details</h2>
        </div>
        <div className="shift-allowance-delete-dialog__body">
          <div className="shift-allowance-delete-dialog__message">
            <img src={alertIcon} alt="" aria-hidden="true" />
            <span>Are you sure you want to delete this shift allowance detail?</span>
          </div>
        </div>
        <div className="shift-allowance-dialog__footer">
          <button
            type="button"
            className="shift-allowance-dialog__cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="shift-allowance-dialog__save"
            onClick={onDelete}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

DeleteShiftAllowanceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

const InitiatorApproverDialog = ({
  form,
  employeeOptions,
  open,
  onChange,
  onClose,
  onSubmit,
}) => {
  if (!open) return null;

  const detailOptions = employeeOptions.map((option) => ({
    ...option,
    label: getEmployeeDetailLabel(option),
  }));
  const findLabel = (empNo) => (
    detailOptions.find((option) => `${option.empNo || option.value}` === `${empNo}`)?.label || ''
  );

  return (
    <div className="shift-allowance-dialog-overlay" role="presentation">
      <div
        className="shift-allowance-approver-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="shift-allowance-approver-title"
      >
        <div className="shift-allowance-dialog__header">
          <h2 id="shift-allowance-approver-title">Initiator &amp; Approver details</h2>
        </div>
        <div className="shift-allowance-approver-dialog__body">
          <ShiftAllowanceDropdownField
            label="Initiator"
            placeholder="Select"
            required
            value={findLabel(form.initiator)}
            options={detailOptions}
            onSelect={(option) => onChange('initiator', option.empNo || option.value)}
          />
          <ShiftAllowanceDropdownField
            label="L1 Approver"
            placeholder="Select"
            required
            helperText="Sr. Manager"
            value={findLabel(form.l1Approver)}
            options={detailOptions}
            onSelect={(option) => onChange('l1Approver', option.empNo || option.value)}
          />
          <ShiftAllowanceDropdownField
            className="shift-allowance-approver-dialog__field--wide"
            label="L2 Approver"
            placeholder="Select"
            required
            helperText="BU Head"
            value={findLabel(form.l2Approver)}
            options={detailOptions}
            onSelect={(option) => onChange('l2Approver', option.empNo || option.value)}
          />
        </div>
        <div className="shift-allowance-dialog__footer">
          <button
            type="button"
            className="shift-allowance-dialog__cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="shift-allowance-dialog__save"
            onClick={onSubmit}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

InitiatorApproverDialog.propTypes = {
  employeeOptions: PropTypes.arrayOf(PropTypes.shape({
    empNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    employeeName: PropTypes.string,
    employeeId: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })).isRequired,
  form: PropTypes.shape({
    initiator: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    l1Approver: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    l2Approver: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  open: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

const UploadShiftAllowanceDialog = ({
  fileName,
  open,
  onClose,
  onDownloadSample,
  onFileChange,
  onUpload,
}) => {
  const fileInputRef = useRef(null);

  if (!open) return null;

  return (
    <div className="shift-allowance-dialog-overlay" role="presentation">
      <div
        className="shift-allowance-upload-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="shift-allowance-upload-title"
      >
        <div className="shift-allowance-upload-dialog__header">
          <h2 id="shift-allowance-upload-title">Upload Shift Allowance Data</h2>
          <button
            type="button"
            className="shift-allowance-upload-dialog__sample"
            onClick={onDownloadSample}
          >
            Sample File
          </button>
        </div>
        <div className="shift-allowance-upload-dialog__body">
          <label className="shift-allowance-upload-dialog__field">
            <span>
              Select file to upload
              <span className="shift-allowance-upload-dialog__required">*</span>
            </span>
            <span className="shift-allowance-upload-dialog__file-row">
              <button
                type="button"
                className="shift-allowance-upload-dialog__choose"
                onClick={() => fileInputRef.current?.click()}
              >
                Choose file
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={onFileChange}
              />
              <span className="shift-allowance-upload-dialog__filename">
                {fileName || 'No file chosen'}
              </span>
            </span>
          </label>
          <div className="shift-allowance-upload-dialog__hint">Accepted format: CSV</div>
        </div>
        <div className="shift-allowance-upload-dialog__footer">
          <button
            type="button"
            className="shift-allowance-dialog__cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="shift-allowance-dialog__save"
            onClick={onUpload}
          >
            Upload
          </button>
        </div>
      </div>
    </div>
  );
};

UploadShiftAllowanceDialog.propTypes = {
  fileName: PropTypes.string,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDownloadSample: PropTypes.func.isRequired,
  onFileChange: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
};

UploadShiftAllowanceDialog.defaultProps = {
  fileName: '',
};

const ShiftAllowance = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const restoreFiltersRef = useRef(location.state?.shiftAllowanceFilters || null);
  const restoreConfigurationRef = useRef(null);
  const { userProfile } = useAuth();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [shiftForm, setShiftForm] = useState(EMPTY_SHIFT_FORM);
  const [editingShiftAllowanceId, setEditingShiftAllowanceId] = useState(null);
  const [deletingShiftAllowanceId, setDeletingShiftAllowanceId] = useState(null);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadFileName, setUploadFileName] = useState('');
  const [isApproverDialogOpen, setIsApproverDialogOpen] = useState(false);
  const [approverForm, setApproverForm] = useState(EMPTY_APPROVER_FORM);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [hasShiftDetails, setHasShiftDetails] = useState(false);
  const [appliedFilterContext, setAppliedFilterContext] = useState({
    initiatorLabel: '',
    initiatorValue: '',
    monthLabel: '',
    monthValue: '',
  });
  const [requestSummary, setRequestSummary] = useState(null);
  const [tableScrollHeight, setTableScrollHeight] = useState('520px');
  const tableStateGetterRef = useRef(null);
  const baseURL = getShiftAllowanceBaseURL();
  const toolbarStatus = hasAppliedFilters ? requestSummary?.requestStatus || '' : '';
  const isRequestOpen = toolbarStatus.toLowerCase() === 'open';

  const userInfo = useMemo(() => ({
    emailId: userProfile?.email || '',
    permissions: userProfile?.permissions || {},
    roleDetails: userProfile?.roleDetails || [],
    employeeId: userProfile?.employeeId || '',
    globalColors: userProfile?.siteConfiguration?.global_colors || {},
  }), [userProfile]);

  const permissions = useMemo(() => {
    const userPermissions = userProfile?.permissions || {};

    return {
      canCreate: hasPermissionAction(userPermissions, MODULE_NAMES.RESOURCE_SHIFT_ALLOWANCE, 'create'),
      canUpdate: hasPermissionAction(userPermissions, MODULE_NAMES.RESOURCE_SHIFT_ALLOWANCE, 'update'),
      canDelete: hasPermissionAction(userPermissions, MODULE_NAMES.RESOURCE_SHIFT_ALLOWANCE, 'delete'),
      canSubmit: hasPermissionAction(userPermissions, MODULE_NAMES.RESOURCE_SHIFT_ALLOWANCE, 'create'),
      canViewApprovalHistory: hasPermissionAction(
        userPermissions,
        MODULE_NAMES.RESOURCE_SHIFT_ALLOWANCE_APPROVAL_HISTORY,
        'read',
      ),
    };
  }, [userProfile?.permissions]);

  const loadEmployeeOptions = useCallback(async () => {
    const response = await fetchShiftAllowanceEmployees();
    const options = Array.isArray(response?.data?.data) ? response.data.data : [];
    setEmployeeOptions(options);
  }, []);

  const openAddDialog = useCallback(() => {
    if (!permissions.canCreate && !permissions.canUpdate) {
      toast.error('You do not have permission to add shift allowance details.');
      return;
    }

    if (isRequestOpen) {
      toast.error('This allowance request is already submitted and is pending approval.');
      return;
    }

    if (!appliedFilterContext.initiatorValue || !appliedFilterContext.monthValue) {
      toast.error('Please select Initiator and Month - Year before adding details.');
      return;
    }

    setShiftForm(EMPTY_SHIFT_FORM);
    setEditingShiftAllowanceId(null);
    setIsAddDialogOpen(true);
    if (employeeOptions.length === 0) {
      loadEmployeeOptions().catch(() => toast.error('Failed to load employee options'));
    }
  }, [
    appliedFilterContext.initiatorValue,
    appliedFilterContext.monthValue,
    employeeOptions.length,
    isRequestOpen,
    loadEmployeeOptions,
    permissions.canCreate,
    permissions.canUpdate,
  ]);

  const updateShiftForm = useCallback((field, value) => {
    setShiftForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleEmployeeSelect = useCallback((employee) => {
    setShiftForm((prev) => ({
      ...prev,
      empNo: employee.empNo || employee.value,
      employeeName: employee.employeeName || '',
      employeeId: employee.employeeId || '',
    }));
  }, []);

  const updateApproverForm = useCallback((field, value) => {
    setApproverForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const openApproverDialog = useCallback(() => {
    setApproverForm({
      ...EMPTY_APPROVER_FORM,
      initiator: appliedFilterContext.initiatorValue,
    });
    setIsApproverDialogOpen(true);
    if (employeeOptions.length === 0) {
      loadEmployeeOptions().catch(() => toast.error('Failed to load employee options'));
    }
  }, [appliedFilterContext.initiatorValue, employeeOptions.length, loadEmployeeOptions]);

  const handleSubmitApproverDetails = useCallback(() => {
    if (!permissions.canSubmit) {
      toast.error('You do not have permission to submit shift allowance request.');
      return;
    }

    const requiredFields = [
      ['initiator', 'Initiator'],
      ['l1Approver', 'L1 Approver'],
      ['l2Approver', 'L2 Approver'],
    ];
    const missingField = requiredFields.find(([field]) => !`${approverForm[field] || ''}`.trim());

    if (missingField) {
      toast.error(`Please select ${missingField[1]}.`);
      return;
    }

    const payload = {
      initiatorEmpNo: approverForm.initiator,
      draftInitiatorEmpNo: appliedFilterContext.initiatorValue,
      allowanceMonth: requestSummary?.allowanceMonth || appliedFilterContext.monthValue,
      l1ApproverEmpNo: approverForm.l1Approver,
      l2ApproverEmpNo: approverForm.l2Approver,
    };

    submitShiftAllowanceApproval(payload)
      .then(async (response) => {
        if (response?.data?.success) {
          toast.success(response.data.message || 'Shift allowance request submitted successfully.');
          setIsApproverDialogOpen(false);
          await tableStateGetterRef.current?.()?.refreshTableData?.();
          return;
        }

        toast.error(
          response?.data?.response?.data?.message
          || response?.data?.message
          || 'Unable to submit shift allowance request.',
        );
      })
      .catch((error) => {
        toast.error(
          error?.response?.data?.message
          || error?.message
          || 'Unable to submit shift allowance request.',
        );
      });
  }, [
    appliedFilterContext.monthValue,
    appliedFilterContext.initiatorValue,
    approverForm,
    permissions.canSubmit,
    requestSummary?.allowanceMonth,
  ]);

  const openUploadDialog = useCallback(() => {
    if (!permissions.canCreate) {
      toast.error('You do not have permission to upload shift allowance details.');
      return;
    }

    if (isRequestOpen) {
      toast.error('This allowance request is already submitted and is pending approval.');
      return;
    }

    setUploadFile(null);
    setUploadFileName('');
    setIsUploadDialogOpen(true);
  }, [isRequestOpen, permissions.canCreate]);

  const openHistoryPage = useCallback(() => {
    if (!permissions.canViewApprovalHistory) {
      toast.error('You do not have permission to view allowance approval history.');
      return;
    }

    const tableState = tableStateGetterRef.current?.();
    const filters = getAppliedFiltersFromConfiguration(tableState?.tableConfiguration || []);
    // Store the selection on the source history entry for browser/app Back.
    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: { ...location.state, shiftAllowanceFilters: filters },
    });
    window.open('/resource/shift-allowance/approval-history', '_blank', 'noopener,noreferrer');
  }, [location, navigate, permissions.canViewApprovalHistory]);

  const closeUploadDialog = useCallback(() => {
    setIsUploadDialogOpen(false);
    setUploadFile(null);
    setUploadFileName('');
  }, []);

  const handleUploadFileChange = useCallback((event) => {
    const selectedFile = event.target.files?.[0] || null;
    event.target.value = '';

    if (!selectedFile) return;
    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload only CSV files.');
      setUploadFile(null);
      setUploadFileName('');
      return;
    }

    setUploadFile(selectedFile);
    setUploadFileName(selectedFile.name);
  }, []);

  const handleDownloadUploadSample = useCallback(async () => {
    try {
      const response = await UseAxios(getProjectFileUploadSampleFile(SHIFT_ALLOWANCE_SAMPLE_FILE_TYPE));

      if (response?.data?.isAxiosError || response?.data instanceof Error) {
        const axiosError = response.data;
        throw new Error(
          axiosError?.response?.data?.message
          || axiosError?.response?.data?.error
          || axiosError?.message
          || 'Failed to download sample file.',
        );
      }

      const signedUrl = response?.data?.data?.signedUrl;

      if (!signedUrl) {
        throw new Error('Download URL not found.');
      }

      const link = document.createElement('a');
      link.href = signedUrl;
      link.style.display = 'none';
      document.body.append(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
    } catch (error) {
      toast.error(error?.message || 'Failed to download sample file.');
    }
  }, []);

  const handleUploadShiftAllowance = useCallback(() => {
    if (!uploadFile) {
      toast.error('Please choose a CSV file to upload.');
      return;
    }

    const initiatorEmpNo = requestSummary?.initiatorEmpNo || appliedFilterContext.initiatorValue;
    const allowanceMonth = requestSummary?.allowanceMonth || appliedFilterContext.monthValue;

    if (!initiatorEmpNo || !allowanceMonth) {
      toast.error('Please select Initiator and Month - Year before uploading details.');
      return;
    }

    parseShiftAllowanceUploadFile(uploadFile)
      .then((rows) => uploadShiftAllowanceDetails({
        initiatorEmpNo,
        allowanceMonth,
        rows,
      }))
      .then(async (response) => {
        if (response?.data?.success) {
          toast.success(response.data.message || 'Shift allowance data uploaded successfully.');
          closeUploadDialog();
          await tableStateGetterRef.current?.()?.refreshTableData?.();
          return;
        }

        const errorList = response?.data?.data?.errors || response?.data?.response?.data?.data?.errors || [];
        const firstErrors = errorList
          .slice(0, 3)
          .map((error) => error.message)
          .join(' ');
        toast.error(
          firstErrors
          || response?.data?.message
          || response?.data?.response?.data?.message
          || 'Unable to upload Shift Allowance file.',
        );
      })
      .catch((error) => {
        toast.error(error?.message || 'Unable to upload Shift Allowance file.');
      });
  }, [
    appliedFilterContext.initiatorValue,
    appliedFilterContext.monthValue,
    closeUploadDialog,
    requestSummary,
    uploadFile,
  ]);

  const toolbarAdditionalElements = useMemo(() => [
    permissions.canViewApprovalHistory || permissions.canCreate ? (
      <ShiftAllowanceMoreMenu
        key="shift-allowance-more-menu"
        isUploadDisabled={isRequestOpen || !permissions.canCreate}
        onHistoryClick={openHistoryPage}
        onUploadClick={openUploadDialog}
        showHistory={permissions.canViewApprovalHistory}
      />
    ) : null,
    hasAppliedFilters && (permissions.canCreate || permissions.canUpdate) ? (
      <ShiftAllowanceAddButton
        key="shift-allowance-add-button"
        disabled={isRequestOpen}
        onClick={openAddDialog}
      />
    ) : null,
  ].filter(Boolean), [
    hasAppliedFilters,
    isRequestOpen,
    openAddDialog,
    openHistoryPage,
    openUploadDialog,
    permissions.canCreate,
    permissions.canUpdate,
    permissions.canViewApprovalHistory,
  ]);

  const toolbarTitle = useMemo(() => (
    hasAppliedFilters && appliedFilterContext.monthLabel
      ? `Allowance Request - ${appliedFilterContext.monthLabel}`
      : 'Allowance Request'
  ), [appliedFilterContext.monthLabel, hasAppliedFilters]);

  const toolbarSubtitle = hasAppliedFilters && requestSummary?.requestDbId
    ? requestSummary.initiatorLabel || ''
    : '';
  const isSubmitDisabled = !hasShiftDetails || isRequestOpen || !permissions.canSubmit;

  const validateShiftForm = useCallback(() => {
    const requiredFields = [
      ['empNo', 'Employee Name'],
      ['employeeId', 'Employee ID'],
      ['groupName', 'Group Name'],
      ['shiftStartTime', 'Shift Start Time'],
      ['shiftEndTime', 'Shift End Time'],
      ['extraHours', 'Extra Hours'],
      ['totalHours', 'Total Hours'],
    ];
    const missingField = requiredFields.find(([field]) => !`${shiftForm[field] || ''}`.trim());

    const resolvedInitiator = requestSummary?.initiatorEmpNo || appliedFilterContext.initiatorValue;
    const resolvedMonth = requestSummary?.allowanceMonth || appliedFilterContext.monthValue;

    if (!resolvedInitiator || !resolvedMonth) {
      return 'Please select Initiator and Month - Year before adding details.';
    }
    if (missingField) return `Please enter ${missingField[1]}.`;

    const numberFields = [
      ['morningShiftDays', 'Morning Shift'],
      ['afternoonShiftDays', 'Afternoon Shift'],
      ['nightShiftDays', 'Night Shift'],
      ['extraHours', 'Extra Hours'],
      ['totalHours', 'Total Hours'],
    ];
    const invalidField = numberFields.find(([field]) => {
      const value = `${shiftForm[field] || ''}`.trim();
      return value && (!Number.isFinite(Number(value)) || Number(value) < 0);
    });

    return invalidField ? `${invalidField[1]} must be a valid number.` : '';
  }, [
    appliedFilterContext.initiatorValue,
    appliedFilterContext.monthValue,
    requestSummary?.allowanceMonth,
    requestSummary?.initiatorEmpNo,
    requestSummary?.requestDbId,
    shiftForm,
  ]);

  const handleSaveShiftDetails = useCallback(async () => {
    if (editingShiftAllowanceId ? !permissions.canUpdate : !permissions.canCreate) {
      toast.error(
        editingShiftAllowanceId
          ? 'You do not have permission to edit shift allowance details.'
          : 'You do not have permission to add shift allowance details.',
      );
      return;
    }

    const validationMessage = validateShiftForm();
    if (validationMessage) {
      toast.error(validationMessage);
      return;
    }

    const payload = {
      initiatorEmpNo: requestSummary?.initiatorEmpNo || appliedFilterContext.initiatorValue,
      allowanceMonth: requestSummary?.allowanceMonth || appliedFilterContext.monthValue,
      empNo: shiftForm.empNo,
      morningShiftDays: shiftForm.morningShiftDays,
      afternoonShiftDays: shiftForm.afternoonShiftDays,
      nightShiftDays: shiftForm.nightShiftDays,
      groupName: shiftForm.groupName,
      shiftStartTime: toBackendTime(shiftForm.shiftStartTime),
      shiftEndTime: toBackendTime(shiftForm.shiftEndTime),
      extraHours: shiftForm.extraHours,
      totalHours: shiftForm.totalHours,
    };

    const response = editingShiftAllowanceId
      ? await updateShiftAllowanceDetail(editingShiftAllowanceId, payload)
      : await createShiftAllowanceDetail(payload);

    if (response?.data?.success) {
      toast.success(
        response.data.message || (
          editingShiftAllowanceId
            ? 'Shift allowance details updated successfully'
            : 'Shift allowance details added successfully'
        ),
      );
      setIsAddDialogOpen(false);
      setEditingShiftAllowanceId(null);
      setShiftForm(EMPTY_SHIFT_FORM);
      await tableStateGetterRef.current?.()?.refreshTableData?.();
      return;
    }

    const message = response?.data?.response?.data?.message
      || response?.data?.message
      || response?.data?.response?.data?.details
      || (
        editingShiftAllowanceId
          ? 'Failed to update shift allowance details.'
          : 'Failed to add shift allowance details.'
      );
    toast.error(message);
  }, [
    appliedFilterContext.initiatorValue,
    appliedFilterContext.monthValue,
    editingShiftAllowanceId,
    permissions.canCreate,
    permissions.canUpdate,
    requestSummary,
    shiftForm,
    validateShiftForm,
  ]);

  const closeDeleteDialog = useCallback(() => {
    setDeletingShiftAllowanceId(null);
  }, []);

  const handleDeleteShiftDetails = useCallback(async () => {
    if (!permissions.canDelete) {
      toast.error('You do not have permission to delete shift allowance details.');
      return;
    }

    if (!deletingShiftAllowanceId) {
      toast.error('Unable to delete this row. Shift allowance id is missing.');
      return;
    }

    const response = await deleteShiftAllowanceDetail(deletingShiftAllowanceId);
    if (response?.data?.success) {
      toast.success(response.data.message || 'Shift allowance details deleted successfully');
      closeDeleteDialog();
      await tableStateGetterRef.current?.()?.refreshTableData?.();
      return;
    }

    toast.error(
      response?.data?.response?.data?.message
      || response?.data?.message
      || 'Failed to delete shift allowance details.',
    );
  }, [closeDeleteDialog, deletingShiftAllowanceId, permissions.canDelete]);

  const handleCellClick = useCallback(({ action, rowData }) => {
    if (!action || rowData?.isTotalRow) return;
    if (action === 'edit') {
      if (!permissions.canUpdate) {
        toast.error('You do not have permission to edit shift allowance details.');
        return;
      }

      if (!rowData?.id) {
        toast.error('Unable to edit this row. Shift allowance id is missing.');
        return;
      }
      setEditingShiftAllowanceId(rowData.id);
      setShiftForm(buildShiftFormFromRow(rowData));
      setIsAddDialogOpen(true);
      if (employeeOptions.length === 0) {
        loadEmployeeOptions().catch(() => toast.error('Failed to load employee options'));
      }
      return;
    }
    if (action === 'delete') {
      if (!permissions.canDelete) {
        toast.error('You do not have permission to delete shift allowance details.');
        return;
      }

      if (!rowData?.id) {
        toast.error('Unable to delete this row. Shift allowance id is missing.');
        return;
      }
      setDeletingShiftAllowanceId(rowData.id);
    }
  }, [employeeOptions.length, loadEmployeeOptions, permissions.canDelete, permissions.canUpdate]);

  const handleFilteredApiResponse = useCallback((response) => {
    const summary = response?.data?.summary ?? response?.data?.data?.summary ?? null;
    normalizeTableUtilityResponse(response);
    if (Array.isArray(response?.data?.tableConfiguration)) {
      const tableConfiguration = response.data.tableConfiguration.map((column) => {
        if (column?.field !== 'action') return column;
        const actionColumn = { ...column };
        delete actionColumn.type;
        delete actionColumn.actions;
        return {
          ...actionColumn,
          filter: false,
          sortable: false,
          pinnable: false,
          align: 'center',
        };
      });
      response.data.tableConfiguration = tableConfiguration;
      if (response.data.data && typeof response.data.data === 'object') {
        response.data.data.tableConfiguration = tableConfiguration;
      }
      response.tableConfiguration = tableConfiguration;
    }
    if (Array.isArray(response?.data?.tableData)) {
      setHasShiftDetails(response.data.tableData.some((row) => row && !row.isTotalRow));
      const tableDataWithActions = response.data.tableData.map((row) => ({
        ...row,
        rowConfiguration: row?.isTotalRow
          ? {
            ...(row.rowConfiguration || {}),
            isFrozen: true,
          }
          : row?.rowConfiguration,
        action: row?.isTotalRow || (!permissions.canUpdate && !permissions.canDelete) ? '' : (
          <ShiftAllowanceRowActions
            canDelete={permissions.canDelete}
            canUpdate={permissions.canUpdate}
            rowData={row}
            onAction={handleCellClick}
          />
        ),
      }));
      response.data.tableData = tableDataWithActions;
      if (response.data.data && typeof response.data.data === 'object') {
        response.data.data.tableData = tableDataWithActions;
      }
      response.tableData = tableDataWithActions;
    }
    setRequestSummary((prev) => (
      JSON.stringify(prev || null) === JSON.stringify(summary || null)
        ? prev
        : summary
    ));
    return response;
  }, [handleCellClick, permissions.canDelete, permissions.canUpdate]);

  useEffect(() => {
    const handleResize = () => {
      setTableScrollHeight(`${Math.max(Math.floor(window.innerHeight - 230), 320)}px`);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      exportHeaderUnmerge: true,
      baseURL,
      showToolBar: true,
      showPagination: false,
      showTieredMenu: true,
      headers: { token: userProfile?.access_token || '' },
      userInfo,
      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: false },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: 'view', enabled: false },
        deleteView: { endpoint: 'view-delete', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: () => 'Shift_Allowance',
          sheetName: () => 'Shift Allowance',
        },
      },
      handler: {
        handleFilteredApiResponse,
        setTableStateHandler: (getTableState) => {
          tableStateGetterRef.current = getTableState;
          const tableState = getTableState?.();
          if (restoreFiltersRef.current?.length && !tableState?.loading
            && tableState?.tableConfiguration?.length) {
            if (!restoreConfigurationRef.current) {
              const savedFilters = restoreFiltersRef.current;
              const restoredConfiguration = tableState.tableConfiguration.map((column) => ({
                ...column,
                menuItems: column.menuItems?.map((menu) => ({
                  ...menu,
                  items: menu.items?.map((item) => ({
                    ...item,
                    ...(item.type === 'filter'
                      ? savedFilters.find((filter) => filter.field === item.field) : {}),
                  })),
                })),
              }));
              restoreConfigurationRef.current = restoredConfiguration;
              tableState.setTableConfiguration(restoredConfiguration);
              return;
            }
            if (tableState.tableConfiguration === restoreConfigurationRef.current) {
              const savedFilters = restoreFiltersRef.current;
              restoreFiltersRef.current = null;
              restoreConfigurationRef.current = null;
              tableState.updateFiltersAndFetch(savedFilters);
            }
          }
          const configFilters = getAppliedFiltersFromConfiguration(tableState?.tableConfiguration || []);
          const stateFilters = Array.isArray(tableState?.filters)
            ? tableState.filters.filter(hasFilterValue)
            : [];
          const appliedFilters = stateFilters.length > 0 ? stateFilters : configFilters;
          const appliedFilterFields = new Set(appliedFilters.map((filter) => filter.field));
          const initiatorFilter = appliedFilters.find((filter) => filter.field === 'initiator');
          const monthFilter = appliedFilters.find((filter) => filter.field === 'monthYear');
          const nextHasAppliedFilters = appliedFilterFields.has('initiator') && appliedFilterFields.has('monthYear');

          setHasAppliedFilters((prev) => (
            prev === nextHasAppliedFilters ? prev : nextHasAppliedFilters
          ));

          setAppliedFilterContext((prev) => {
            const nextContext = nextHasAppliedFilters
              ? {
                initiatorLabel: getFilterValue(initiatorFilter),
                initiatorValue: getFilterRawValue(initiatorFilter),
                monthLabel: formatMonthHeading(getFilterValue(monthFilter)),
                monthValue: getFilterRawValue(monthFilter),
              }
              : { initiatorLabel: '', initiatorValue: '', monthLabel: '', monthValue: '' };

            return prev.initiatorLabel === nextContext.initiatorLabel
              && prev.initiatorValue === nextContext.initiatorValue
              && prev.monthLabel === nextContext.monthLabel
              && prev.monthValue === nextContext.monthValue
              ? prev
              : nextContext;
          });

          if (!nextHasAppliedFilters) {
            setRequestSummary(null);
            setHasShiftDetails(false);
          }
        },
        onCellClickHandler: handleCellClick,
      },
      toolBarSettings: {
        showFilterOption: true,
        showColumnFilterMenu: true,
        showAddNew: false,
        showExpandCollapse: false,
        showDownloadExcel: true,
        downloadExcelSheetName: 'Shift Allowance',
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showToolBarHeading: true,
        toolBarHeading: (
          <ShiftAllowanceToolbarHeading
            title={toolbarTitle}
            status={toolbarStatus}
            statusClassName={toolbarStatus ? getStatusClassName(toolbarStatus) : ''}
            subtitle={toolbarSubtitle}
          />
        ),
        showReset: false,
        showFilters: true,
        additionalElements: toolbarAdditionalElements,
      },
    },
    tableSettings: {
      size: 'small',
      pageSize: PAGE_SIZE,
      tableScrollHeight: hasAppliedFilters ? SHIFT_ALLOWANCE_TABLE_HEIGHT : tableScrollHeight,
      loaderSkeleton: { rows: PAGE_SIZE, bodyCells: 2, columns: 10 },
      paginationConfig: PAGINATION_CONFIG,
      showExpandCollapse: false,
      maxFrozenColumns: { desktop: 1, tablet: 1, mobile: 1 },
    },
  }), [
    baseURL,
    appliedFilterContext.monthLabel,
    toolbarStatus,
    toolbarSubtitle,
    toolbarTitle,
    handleFilteredApiResponse,
    handleCellClick,
    hasAppliedFilters,
    tableScrollHeight,
    toolbarAdditionalElements,
    userInfo,
    userProfile?.access_token,
  ]);

  const tableUtilityClassName = [
    'shift-allowance-table-utility',
    !hasAppliedFilters ? 'shift-allowance-empty' : 'shift-allowance-has-data',
  ].join(' ');

  return (
    <div className="shift-allowance-container">
      <div className="shift-allowance-table-shell">
        <div className={tableUtilityClassName}>
          <TableUtility
            configuration={configuration}
            emptyStateComponent={!hasAppliedFilters ? (
              <EmptyState message="Select the filters above to see the results" />
            ) : undefined}
            showFilterOption
          />
          {hasAppliedFilters && (
            <div className="shift-allowance-footer">
              <button type="button" className="shift-allowance-footer__cancel">
                Cancel
              </button>
              <button
                type="button"
                className="shift-allowance-footer__submit"
                disabled={isSubmitDisabled}
                onClick={openApproverDialog}
              >
                Submit
              </button>
            </div>
          )}
          <AddShiftDetailsDialog
            employeeOptions={employeeOptions}
            form={shiftForm}
            open={isAddDialogOpen}
            title={editingShiftAllowanceId ? 'Edit Shift Details' : 'Add Shift Details'}
            onClose={() => {
              setIsAddDialogOpen(false);
              setEditingShiftAllowanceId(null);
            }}
            onChange={updateShiftForm}
            onEmployeeSelect={handleEmployeeSelect}
            onSave={handleSaveShiftDetails}
          />
          <UploadShiftAllowanceDialog
            fileName={uploadFileName}
            open={isUploadDialogOpen}
            onClose={closeUploadDialog}
            onDownloadSample={handleDownloadUploadSample}
            onFileChange={handleUploadFileChange}
            onUpload={handleUploadShiftAllowance}
          />
          <DeleteShiftAllowanceDialog
            open={Boolean(deletingShiftAllowanceId)}
            onClose={closeDeleteDialog}
            onDelete={handleDeleteShiftDetails}
          />
          <InitiatorApproverDialog
            employeeOptions={employeeOptions}
            form={approverForm}
            open={isApproverDialogOpen}
            onChange={updateApproverForm}
            onClose={() => setIsApproverDialogOpen(false)}
            onSubmit={handleSubmitApproverDetails}
          />
        </div>
      </div>
    </div>
  );
};

export default ShiftAllowance;
